import { useEffect, useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useLocation } from 'react-router-dom';
import isEqual from 'lodash.isequal';
import {
  SidePanel,
  ButtonPrimary,
  ButtonPseudo,
  PermissionEnum,
  Popup,
  ResponseError,
  PermissionDenied,
  responseErrorsResolver,
  SidePanelType,
} from '@ui/src';
import { useAppSelector, useAppDispatch } from '@store';
import { clearMarginPanelState } from '../../../hoc/customers/store/slices/tariffs/tariff';
import {
  useCustomersCreateAmountsMutation,
  useLazyGetCustomerMarginQuery,
  useUpdateCustomerMarginMutation,
  useLazyGetCustomMarginsQuery,
  useUpdateCustomerAmountMutation,
  useRestoreCustomMarginToDefaultMutation,
  useCreateCustomerMarginMutation,
} from '../../../hoc/customers/store/services/customers';
import MarginPanelContent from './margin-panel-content';
import {
  MarginFormValues,
  MarginAmount,
  CustomMargin,
  CustomerStatus,
} from '../../../hoc/customers/types';
import { MarginDetailsTypes } from './types';

const MarginDetails = ({
  panelIsOpen,
  setPanelIsOpen,
  allMarginsVisible,
  selectedCurrency,
  customerId,
  selectedMarginId,
  defaultMargin,
  tariffId,
  status,
}: MarginDetailsTypes): React.JSX.Element => {
  const {
    customerTariffs: { customMargin: marginValues },
    customersDictionaries: { activePermissions },
  } = useAppSelector((state) => state);
  const [dirtyPopupVisible, setDirtyPopupVisible] = useState(false);

  const [defaultAmounts, setDefaultAmounts] = useState<MarginAmount[]>([]);
  const [amountsList, setAmounts] = useState<MarginAmount[]>();
  const [initialMarginAmounts, setInitialMarginAmounts] = useState<
    MarginAmount[]
  >([]);
  const [updateIsSuccess, setUpdateIsSuccess] = useState(false);
  const [marginFeeError, setMarginFeeError] = useState('');
  const [globalError, setGlobalError] = useState('');
  const [restoreConfirmationPopupVisible, setRestoreConfrimationPopupVisible] =
    useState(false);
  const [restore, { isLoading: restoreInProgress }] =
    useRestoreCustomMarginToDefaultMutation();

  const dispatch = useAppDispatch();

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    setError,
    formState: { errors, isDirty },
  } = useForm<MarginFormValues>();

  const amounts = watch('amounts');
  const fields = watch();

  const [getMargin, { isLoading: marginIsLoading, isFetching }] =
    useLazyGetCustomerMarginQuery();
  const [updateMargin, { isLoading: updateInProgress }] =
    useUpdateCustomerMarginMutation();
  const [createMargin, { isLoading: createInProgress }] =
    useCreateCustomerMarginMutation();
  const [
    createAmounts,
    { isLoading: createAmountsInProgress, isSuccess: createAountsIsSuccess },
  ] = useCustomersCreateAmountsMutation();
  const [getMargins] = useLazyGetCustomMarginsQuery();
  const [updateAmount] = useUpdateCustomerAmountMutation();

  const { search } = useLocation();

  const handleClose = useCallback(() => {
    dispatch(clearMarginPanelState());
    setPanelIsOpen(false);
    setDirtyPopupVisible(false);
    setAmounts(undefined);
  }, []);

  const marginsIsEqual = isEqual(
    {
      targetAccountGainsId: fields.targetAccountGainsId,
      targetAccountLossesId: fields.targetAccountLossesId,
      amounts: fields.amounts,
      rateSourceId: fields.rateSourceId,
    },
    {
      targetAccountGainsId: marginValues?.targetAccountGainsId,
      targetAccountLossesId: marginValues?.targetAccountLossesId,
      amounts:
        marginValues?.amounts?.edges.map(
          (item: { node: MarginAmount }) => item.node,
        ) || [],
      rateSourceId: marginValues?.rateSourceId,
    },
  );

  const onFormSubmit = handleSubmit(async (values) => {
    setGlobalError('');

    const newAmounts = values?.amounts
      ?.filter((amount) =>
        defaultAmounts.every((item) => amount.id !== item.id),
      )
      .filter(
        (item) => item.feePercent.toString() && item.fromAmount.toString(),
      );

    const updatedAmounts = values.amounts?.filter((amount) =>
      defaultAmounts.every((item) => !isEqual(amount, item)),
    );

    let response: any;

    const body: {
      rateSourceId: string;
      currencyFromId: string;
      currencyToId: string;
      organizationId: string;
      defaultMarginId?: string;
    } = {
      rateSourceId: values.rateSourceId as string,
      currencyFromId: values.currencyFromId as string,
      currencyToId: values.currencyToId as string,
      organizationId: customerId as string,
    };

    if (
      !marginValues?.isCustom ||
      (marginValues.isCustom && !marginValues.organizationId)
    ) {
      body.defaultMarginId = defaultMargin?.id;

      response = (await createMargin(body)) as {
        data: { data: CustomMargin };
      };

      if (response?.data?.data) {
        response = await createAmounts({
          body: {
            items: values?.amounts
              .filter(
                (item) =>
                  item.feePercent.toString() && item.fromAmount.toString(),
              )
              .map((item: MarginAmount) => ({
                feePercent: Number(item.feePercent),
                fromAmount: Number(item.fromAmount),
                marginId: response?.data?.data?.id as string,
              })),
          },
        });
      }
    } else if (marginValues.isCustom) {
      response = (await updateMargin({
        id: marginValues?.id,
        body,
      })) as { error: ResponseError };

      if (newAmounts.length) {
        response = await createAmounts({
          body: {
            items: newAmounts.map((item: MarginAmount) => ({
              feePercent: Number(item.feePercent),
              fromAmount: Number(item.fromAmount),
              marginId: marginValues?.id,
            })),
          },
        });
      } else {
        setUpdateIsSuccess(true);
      }
    }

    if (!(response as { error: ResponseError }).error) {
      setUpdateIsSuccess(true);

      setTimeout(() => {
        let queryValue: string;

        if (!allMarginsVisible) {
          queryValue = `?organizationId=${customerId}&createType=custom&take=6`;
        } else {
          queryValue = `?currencyFromId=${selectedCurrency}&organizationId=${customerId}&tariffId=${tariffId}&take=20`;

          if (search) {
            queryValue = `${queryValue}${search?.replace('?', '&')}`;
          }
        }

        getMargins(queryValue);
        handleClose();
      }, 300);
    } else if ((response as { error: ResponseError }).error) {
      responseErrorsResolver({
        errors: (response as { error: ResponseError }).error,
        setError,
        setGlobalError,
      });

      if (
        (response as { error: ResponseError }).error.data?.message[0] &&
        (response as { error: ResponseError }).error.data.message[0] // @ts-ignore
          ?.property === 'coreError'
      ) {
        const error = // @ts-ignore
          (response as { error: ResponseError }).error.data.message[0]?.value;

        if (error && error.includes('Float')) {
          setMarginFeeError('Fee must be a number');
        }
      }
    }

    if (updatedAmounts.length && marginValues?.isCustom) {
      await Promise.all(
        updatedAmounts
          .filter((item) => !item?.id?.includes('amount'))
          .map(async (item) => {
            await updateAmount({
              id: item.id as string,
              body: {
                fromAmount: item.fromAmount,
                feePercent: item.feePercent,
              },
            });
          }),
      );
    }
  });

  const onRestoreToDefault = async () => {
    const response = restore(marginValues?.id as string) as {
      data?: { data: CustomMargin };
      error?: ResponseError;
    };

    if (response && !(response as { error: ResponseError }).error) {
      setTimeout(() => {
        let queryValue: string;

        if (!allMarginsVisible) {
          queryValue = `?organizationId=${customerId}&createType=custom&take=6`;
        } else {
          queryValue = `?currencyFromId=${selectedCurrency}&organizationId=${customerId}&tariffId=${tariffId}&take=20`;

          if (search) {
            queryValue = `${queryValue}${search?.replace('?', '&')}`;
          }
        }

        getMargins(queryValue);
        handleClose();
      }, 300);
    } else if ((response as { error: ResponseError }).error) {
      responseErrorsResolver({
        errors: (response as { error: ResponseError }).error,
        setError,
        setGlobalError,
      });
    }
  };

  const onResetMargins = () => {
    setRestoreConfrimationPopupVisible(false);

    reset({
      ...fields,
      targetAccountGainsId: fields.targetAccountGainsId,
      targetAccountLossesId: fields.targetAccountLossesId,
      amounts: defaultMargin?.amounts?.edges.map(
        (item: { node: MarginAmount }) => item.node,
      ),
    });

    setAmounts(
      defaultMargin?.amounts?.edges
        .map((item: { node: MarginAmount }) => item.node)
        .sort((a, b) => a.fromAmount - b.fromAmount),
    );

    onRestoreToDefault();
  };

  useEffect(() => {
    if (panelIsOpen && selectedMarginId) {
      getMargin(selectedMarginId);
    }
  }, [panelIsOpen, selectedMarginId]);

  useEffect(() => {
    if (
      marginValues?.amounts &&
      marginValues.amounts.edges.length &&
      !amountsList?.length
    ) {
      setAmounts(
        marginValues?.amounts.edges
          .map((item: { node: MarginAmount }) => item.node)
          .sort(
            (a: MarginAmount, b: MarginAmount) => a.fromAmount - b.fromAmount,
          ),
      );
    } else if (
      marginValues?.amounts &&
      !marginValues?.amounts?.edges?.length &&
      !amountsList?.length
    ) {
      setAmounts([
        {
          marginId: 'margin-0',
          fromAmount: 0,
          feePercent: 0,
          id: 'amount-0',
        },
      ]);
    }
  }, [marginValues?.amounts, amountsList]);

  useEffect(() => {
    if (marginValues) {
      let body: MarginFormValues = {
        targetAccountGainsId: marginValues.targetAccountGainsId || '',
        targetAccountLossesId: marginValues.targetAccountLossesId || '',
        amounts:
          marginValues?.amounts?.edges.map(
            (item: { node: MarginAmount }) => item.node,
          ) || [],
      };

      setInitialMarginAmounts(
        marginValues?.amounts?.edges.map(
          (item: { node: MarginAmount }) => item.node,
        ) || [],
      );

      body = {
        ...body,
        rateSourceId: marginValues.rateSourceId,
        currencyFromId: marginValues.currencyFromId,
        currencyToId: marginValues.currencyToId,
        tariffId: marginValues.tariffId,
      };

      reset(body);

      setDefaultAmounts(
        !marginValues.isCustom
          ? []
          : (marginValues?.amounts?.edges.map(
              (item) => item.node,
            ) as MarginAmount[]) || [],
      );
    }
  }, [marginValues]);

  useEffect(() => {
    if (updateIsSuccess || createAountsIsSuccess) {
      handleClose();
    }
  }, [updateIsSuccess, createAountsIsSuccess]);

  let titleText: string;

  if (marginIsLoading || isFetching) {
    titleText = 'Loading...';
  } else {
    titleText = `${marginValues?.currencyFrom?.name}-${marginValues?.currencyTo?.name} FX margin`;
  }

  let content;

  if (
    !activePermissions?.includes(
      PermissionEnum.SETTINGS_EXCHANGE_EDIT_MARGINS_ACTION,
    )
  ) {
    content = <PermissionDenied />;
  } else {
    content = (
      <MarginPanelContent
        control={control}
        errors={errors}
        isLoading={marginIsLoading || isFetching}
        amounts={amountsList}
        setValue={setValue}
        isEditable={activePermissions?.includes(
          PermissionEnum.SETTINGS_EXCHANGE_EDIT_MARGINS_ACTION,
        )}
        isCustom={marginValues?.isCustom}
        setAmounts={setAmounts}
        marginId={selectedMarginId}
        marginFeeError={marginFeeError}
        auditLogAvailable={activePermissions?.includes(
          PermissionEnum.AUDIT_LOG_LIST,
        )}
        onResetMargins={setRestoreConfrimationPopupVisible}
        userMargin={Boolean(
          marginValues?.isCustom && marginValues.organizationId,
        )}
        restoreInProgress={restoreInProgress}
        currencyFromId={fields?.currencyFromId}
        status={status}
      />
    );
  }

  return (
    <>
      <SidePanel
        closeOnClickOutside
        isOpen={panelIsOpen}
        type={SidePanelType.Exchange}
        onClose={
          isDirty ||
          (selectedMarginId && !isEqual(initialMarginAmounts, amounts))
            ? () => setDirtyPopupVisible(true)
            : handleClose
        }
        title={titleText}
        sidepanelExpandedKey="customers-margin"
        errorSidepanel={globalError}
        footer={
          <>
            {activePermissions?.includes(
              PermissionEnum.SETTINGS_EXCHANGE_EDIT_MARGINS_ACTION,
            ) &&
              status !== CustomerStatus.CLOSED && (
                <ButtonPrimary
                  text={
                    createInProgress || updateInProgress || restoreInProgress
                      ? 'Loading...'
                      : 'Save'
                  }
                  onClick={onFormSubmit}
                  disabled={
                    createInProgress ||
                    updateInProgress ||
                    createAmountsInProgress ||
                    Boolean(
                      marginsIsEqual &&
                      marginValues?.isCustom &&
                      marginValues.organizationId,
                    ) ||
                    restoreInProgress
                  }
                  bold
                />
              )}

            <ButtonPseudo
              text="Cancel"
              onClick={
                isDirty ||
                (selectedMarginId && !isEqual(initialMarginAmounts, amounts))
                  ? () => setDirtyPopupVisible(true)
                  : handleClose
              }
              bold
            />
          </>
        }
      >
        {content}
      </SidePanel>
      <Popup
        title="You have unsaved changes that will be lost"
        description="Are you sure you want to close this form?"
        onCancelClick={() => setDirtyPopupVisible(false)}
        onOkClick={handleClose}
        cancelButtonText="Cancel"
        okButtonText="Close"
        isVisible={dirtyPopupVisible}
        parent={document.getElementById('dirty-popup') as HTMLElement}
      />
      <Popup
        title="Are you sure you want to restore to default this margin?"
        onCancelClick={() => setRestoreConfrimationPopupVisible(false)}
        onOkClick={onResetMargins}
        cancelButtonText="Cancel"
        okButtonText="Yes, restore"
        isVisible={restoreConfirmationPopupVisible}
        parent={document.getElementById('dirty-popup') as HTMLElement}
      />
      <div id="dirty-popup" />
    </>
  );
};

export default MarginDetails;
